import { type Options } from '@contentful/rich-text-react-renderer'
import { INLINES, MARKS } from '@contentful/rich-text-types'
import { type ReactNode } from 'react'
import styled from 'styled-components'

import { TextLink } from '@syconium/little-miss-figgy/dist/components/TextLink'

import { requiresFullPageLoad } from '../../../../../lib/adapters/next-routing-service/utils'
import { sanitizeContentfulRichDocument } from '../../../../../lib/utils/contentful'
import { NextLink } from '../../../navigation/NextLink'

const UnderlinedRichText = styled.span`
  text-decoration: underline;
`

type HyperlinkRenderer = (props: { href: string; children: ReactNode }) => ReactNode

// Default inline link renderer for rich-text hyperlinks.
const defaultHyperlinkRenderer: HyperlinkRenderer = ({ href, children }) => (
  <TextLink
    as={requiresFullPageLoad(href) ? 'a' : NextLink}
    href={href}
    inheritTypography
    variant='underline'
  >
    {children}
  </TextLink>
)

// Sanitizes a Contentful document and returns it only when it still has content.
export const getNonEmptySanitizedContentfulDocument = (contentfulDocument: unknown) => {
  const sanitizedDocument = sanitizeContentfulRichDocument(contentfulDocument)
  return sanitizedDocument.content.length > 0 ? sanitizedDocument : undefined
}

// Walks a rich-text node tree and returns plain text.
export const getContentfulRichTextPlainText = (node: unknown): string => {
  if (node && typeof node === 'object' && 'value' in node && typeof node.value === 'string') {
    return node.value
  }

  if (node && typeof node === 'object' && 'content' in node && Array.isArray(node.content)) {
    return node.content.map(getContentfulRichTextPlainText).join(' ')
  }

  return ''
}

// Creates default mark renderers with optional underline override.
export const createDefaultRichTextRenderMark = (
  renderUnderline: (children: ReactNode) => ReactNode = children => (
    <UnderlinedRichText>{children}</UnderlinedRichText>
  )
): NonNullable<Options['renderMark']> => ({
  [MARKS.BOLD]: children => <strong>{children}</strong>,
  [MARKS.ITALIC]: children => <em>{children}</em>,
  [MARKS.UNDERLINE]: children => renderUnderline(children),
})

// Creates a hyperlink node renderer with configurable link component behavior.
export const createRichTextHyperlinkRenderer = (
  renderHyperlink: HyperlinkRenderer = defaultHyperlinkRenderer
): NonNullable<Options['renderNode']>[typeof INLINES.HYPERLINK] => {
  return (node, children) => {
    const href = typeof node.data.uri === 'string' ? node.data.uri : undefined
    if (!href) return children
    return renderHyperlink({ href, children })
  }
}
